import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

/**
 * *"Do you need a new pay card?"* — the arrival menu's pay-card row for a returning candidate whose record
 * already holds a card [Operator, 2026-10-16: «10a- A», to be recorded as decision 0508; Figma
 * `Edge cases / E7 · Pay card — do you need a new one? (returning) — Desktop`, resolved by NAME].
 *
 * **Reached only from the menu, and only when `usePayCardOnFile()` is `true`.** Without a card on file —
 * `false`, or unknown — the row lands on R1, {@link SCREEN.payCardAsk}, unchanged [KPC-1f's fail-open].
 *
 * **One action, and Back is the "no"** — R1's pattern, and the frame's own trigger note: *"Answering No
 * returns to the hub."* A candidate who has their card leaves through Back to the menu, having raised
 * nothing [0177]. ⛔ Do not add a *No* / *skip* control, or R1's *Request assistance*: the frame draws
 * neither, and the *Need help?* every screen carries is the way to a person.
 *
 * ⚠️ **Two places where this is not the frame, both recorded in `design-qa/known-departures.md`:**
 * - **The header row (Back + Kiosk badge).** E7 draws none, so as drawn the screen has no way back to the
 *   menu but *Exit*, which ends the visit — a dead end for the candidate the copy tells "there's nothing to
 *   do here" [BR-K7]. The row is R1's, so the content sits 48px lower at 1440 than E7 draws it.
 * - **820 and 375.** The file draws E7 at Desktop only, as it does every `Edge cases` frame. Those widths
 *   follow R1 (`06a · Pay card (returning)`), drawn at all three with the same card and column, as
 *   `SubmissionFailedScreen` follows `04 · See a rep` for E4.
 */
export function PayCardNeedScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  // `arrival` is set on the only route here, so Back takes `useBack`'s menu branch and queues nothing.
  const back = useBack();

  return (
    <KioskScreen>
      <KioskCard
        header={
          <>
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
        rhythm="flush"
      >
        {/*
          `Content`: pad T32 B32, the `Heading` block — eyebrow 16, heading 28, helper 20, 8 apart — then 24
          to the one 52-tall full-width button.
        */}
        <div className="flex w-full flex-col items-start gap-6 py-8">
          <div className="flex w-full flex-col items-start gap-2">
            <p className="w-full text-xs leading-4 tracking-[1.44px] text-[var(--muted-foreground)]">
              {t('payCardNeed.eyebrow')}
            </p>
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-[var(--foreground)]"
              ref={heading}
              tabIndex={-1}
            >
              {t('payCardNeed.question')}
            </h1>
            <p className="w-full text-sm leading-5 text-[var(--muted-foreground)]">
              {t('payCardNeed.helper')}
            </p>
          </div>

          <Button
            fullWidth
            onClick={() => {
              goTo(SCREEN.payCard);
            }}
          >
            {t('payCardNeed.yes')}
          </Button>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
